<div class="sp-folders" data-testid="sharepoint-folders">
    <!-- Header -->
    <div class="sp-folders__head">
        <p class="sp-folders__title" data-testid="sharepoint-title">
            {{ 'aumico_integration_sharepoint_title' | i18next }}
        </p>
    </div>

    <!-- Document-source tiles. Always rendered; the selected one carries aria-current. -->
    <div class="sp-folders__tiles" role="list">
        <button
            *ngFor="let provider of providers"
            type="button"
            class="sp-folders__tile"
            role="listitem"
            [class.sp-folders__tile--selected]="provider.id === selectedProviderId"
            [class.sp-folders__tile--disabled]="!provider.enabled"
            [disabled]="!provider.enabled"
            [attr.aria-current]="provider.id === selectedProviderId ? 'true' : null"
            [attr.data-testid]="'doc-source-tile-' + provider.id"
            (click)="onSelectProvider(provider)"
        >
            <lucide-icon [name]="provider.icon" [size]="20" aria-hidden="true" class="sp-folders__tile-icon"></lucide-icon>
            <span class="sp-folders__tile-label">{{ provider.labelKey | i18next }}</span>
            <span class="sp-folders__tile-desc">{{ provider.descriptionKey | i18next }}</span>
            <!-- The connection, as a badge on its own tile rather than a bar under it. -->
            <span
                class="sp-folders__tile-badge"
                data-testid="doc-source-connected"
                *ngIf="provider.id === 'sharepoint' && connection.status === 'connected'"
                >{{ 'aumico_integration_sharepoint_connected_banner' | i18next }}</span
            >
            <!-- A source that is not offered yet says so, rather than only looking greyed out. -->
            <span class="sp-folders__tile-soon" data-testid="doc-source-soon" *ngIf="!provider.enabled">{{
                'aumico_companies_integrations_section_coming_soon' | i18next
            }}</span>
        </button>
    </div>

    <!-- SharePoint panel -->
    <div *ngIf="selectedProviderId === 'sharepoint'" class="sp-folders__panel" data-testid="sharepoint-panel">
        <!-- disconnected / error: explainer + connect button -->
        <div
            *ngIf="connection.status === 'disconnected' || connection.status === 'error'"
            class="sp-folders__connect"
        >
            <p class="sp-folders__explainer" data-testid="sharepoint-description">
                {{ 'aumico_integration_sharepoint_description' | i18next }}
            </p>

            <div
                *ngIf="connection.status === 'error'"
                class="sp-folders__error"
                data-testid="sharepoint-error"
                data-status="error"
            >
                {{ (connection.errorMessageKey || 'aumico_integration_sharepoint_error') | i18next }}
            </div>

            <ham-button
                variant="primary"
                size="sm"
                data-testid="sharepoint-connect"
                (buttonClick)="onConnect()"
            >
                {{ 'aumico_integration_sharepoint_connect' | i18next }}
            </ham-button>
        </div>

        <!--
            awaiting-consent: aumico's OWN pre-consent card (REV-21057). Shown after the
            connect click and BEFORE any Microsoft popup — the tenant may have granted
            admin consent, in which case Microsoft skips its consent screen and this is
            the only place the user learns what aumico requests.
        -->
        <div
            *ngIf="connection.status === 'awaiting-consent'"
            class="sp-folders__consent"
            data-testid="sharepoint-consent-card"
            data-status="awaiting-consent"
        >
            <p class="sp-folders__consent-heading" data-testid="sharepoint-consent-heading">
                {{ 'aumico_integration_sharepoint_consent_heading' | i18next }}
            </p>

            <p class="sp-folders__consent-label" data-testid="sharepoint-consent-permissions-label">
                {{ 'aumico_integration_sharepoint_consent_permissions_label' | i18next }}
            </p>

            <ul class="sp-folders__consent-list">
                <li
                    *ngFor="let permission of requestedPermissions"
                    class="sp-folders__consent-permission"
                    data-testid="sharepoint-consent-permission"
                >
                    <lucide-icon
                        name="check"
                        [size]="16"
                        aria-hidden="true"
                        class="sp-folders__consent-permission-icon"
                    ></lucide-icon>
                    <span class="sp-folders__consent-permission-body">
                        <span class="sp-folders__consent-permission-label">
                            {{ permission.labelKey | i18next }}
                        </span>
                        <span
                            *ngIf="permission.descriptionKey"
                            class="sp-folders__consent-permission-desc"
                        >{{ permission.descriptionKey | i18next }}</span>
                    </span>
                </li>
            </ul>

            <p class="sp-folders__consent-footnote" data-testid="sharepoint-consent-footnote">
                {{ 'aumico_integration_sharepoint_consent_footnote' | i18next }}
            </p>

            <div class="sp-folders__consent-actions">
                <ham-button
                    variant="primary"
                    size="sm"
                    data-testid="sharepoint-consent-allow"
                    (buttonClick)="onAllowConsent()"
                >
                    {{ 'aumico_integration_sharepoint_consent_allow' | i18next }}
                </ham-button>
                <ham-button
                    variant="primary"
                    size="sm"
                    [outline]="true"
                    data-testid="sharepoint-consent-cancel"
                    (buttonClick)="onCancelConsent()"
                >
                    {{ 'aumico_integration_sharepoint_consent_cancel' | i18next }}
                </ham-button>
            </div>
        </div>

        <!-- connecting: in-progress indicator -->
        <div *ngIf="connection.status === 'connecting'" class="sp-folders__connecting" data-testid="sharepoint-connecting">
            {{ 'aumico_integration_sharepoint_connecting' | i18next }}
        </div>

        <!-- connected: folder list + add-folder. The connection itself shows as the tile's badge. -->
        <ng-container *ngIf="connection.status === 'connected'">
            <!-- Connected is not ready: without a folder there is nothing to read. Said once, until there is one. -->
            <p class="sp-folders__hint" data-testid="sharepoint-add-folder-hint" *ngIf="folders.length === 0">
                {{ 'aumico_integration_sharepoint_add_folder_hint' | i18next }}
            </p>

            <ul class="sp-folders__list">
                <li
                    *ngFor="let folder of folders"
                    class="sp-folders__folder"
                    data-testid="selected-folder"
                    [attr.data-folder-id]="folder.id"
                >
                    <lucide-icon name="folder" [size]="18" aria-hidden="true" class="sp-folders__folder-icon"></lucide-icon>

                    <div class="sp-folders__folder-body">
                        <span class="sp-folders__folder-path" data-testid="selected-folder-path">{{ folder.path }}</span>

                        <div class="sp-folders__folder-meta">
                            <span class="sp-folders__folder-source" data-testid="selected-folder-source">{{ folder.source }}</span>
                            <span
                                *ngIf="folder.fileCount !== undefined && folder.fileCount !== null"
                                class="sp-folders__folder-count"
                                data-testid="selected-folder-file-count"
                            >{{ folder.fileCount }}</span>
                            <label class="sp-folders__folder-subfolders">
                                <input
                                    type="checkbox"
                                    class="sp-folders__folder-subfolders-input"
                                    data-testid="selected-folder-subfolders-toggle"
                                    [checked]="folder.includeSubfolders"
                                    (change)="onToggleSubfolders(folder.id, $any($event.target).checked)"
                                />
                                <span data-testid="selected-folder-subfolders">{{
                                    'aumico_integration_sharepoint_include_subfolders' | i18next
                                }}</span>
                            </label>
                        </div>
                    </div>

                    <button
                        type="button"
                        class="sp-folders__folder-remove"
                        data-testid="selected-folder-remove"
                        [attr.aria-label]="'aumico_integration_sharepoint_remove' | i18next"
                        (click)="onRemoveFolder(folder.id)"
                    >
                        <lucide-icon name="x" [size]="16" aria-hidden="true"></lucide-icon>
                    </button>
                </li>
            </ul>

            <ham-button
                variant="gray"
                size="sm"
                data-testid="add-folder"
                (buttonClick)="onAddFolder()"
            >
                {{ 'aumico_integration_sharepoint_add_folder' | i18next }}
            </ham-button>

            <div
                *ngIf="addFolderErrorKey"
                class="sp-folders__error"
                role="alert"
                data-testid="sharepoint-add-folder-error"
                data-status="error"
            >
                {{ addFolderErrorKey | i18next }}
            </div>
        </ng-container>
    </div>
</div>
